import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import { API_ROUTE, Routes } from "../../Helpers/Constants";
import AuthenticationContext from "../../Store/Authentication-Context";
import { NavBar } from "../NavBar";
import ConnectionForm from "../BillingSyncSteps/ConnectionForm";
import CustomerAgreementGrid from "../BillingSyncSteps/CustomerAgreementGrid";
import ProductMappingGrid from "../BillingSyncSteps/ProductMappingGrid";
import DryRunPanel from "../BillingSyncSteps/DryRunPanel";
import ActivationPanel from "../BillingSyncSteps/ActivationPanel";
import ResellerPicker, {
    ResellerPickerUnavailable,
    ChooseResellerPrompt,
} from "../BillingSyncSteps/ResellerPicker";
import PanelState from "../MainComponents/PanelState";
import * as BillingSyncApi from "../../Helpers/BillingSyncApi";

/**
 * Task 37 — the ConnectWise sync setup page.
 *
 * Phase 1 is the connection only. Product mapping and customer agreements join this page as further
 * tabs in Phase 2, which is why the tab strip exists now with one tab in it: adding the others should
 * not mean restructuring the page around them.
 *
 * Shaped on Pages/ManageSuppressions.js — the mount-time token check, the loading/empty/content
 * render, toast for feedback.
 */

/** The tabs, in the order a reseller works through them. */
const TAB = {
    CONNECTION: "connection",
    CUSTOMERS: "customers",
    PRODUCTS: "products",
    PREVIEW: "preview",
    ACTIVATION: "activation",
};

/**
 * How often the screen says "still here".
 *
 * Presence counts as current for ten minutes on the server, so this is comfortably inside it:
 * a missed call must not make a colleague look as though they have left.
 */
const PRESENCE_INTERVAL_MS = 2 * 60 * 1000;

/** How often to look for a preview that is still being prepared. */
const DRY_RUN_POLL_MS = 3000;

const EMPTY_FORM = { siteUrl: "", companyId: "", publicKey: "", privateKey: "", clientId: "" };

/**
 * Master administrators may work on any reseller; everyone else only on their own.
 *
 * Read from the session purely to decide whether to offer the selector. The API decides the reseller
 * from the signed token and confirms entitlement server-side, so a browser claiming otherwise gains
 * nothing — and DeCryptParameter.js is a no-op, so this value is not protected either.
 */
function isMasterAdministrator() {
    try {
        return sessionStorage.getItem("ResellerID") === "1";
    } catch (e) {
        return false;
    }
}

export default function ConnectWiseSync() {
    const authContext = useContext(AuthenticationContext);
    const { setAuthContext } = authContext;

    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);

    // Null means "my own reseller", which is every case except a master administrator who has
    // chosen somebody else.
    const [tab, setTab] = useState(TAB.CONNECTION);

    // Phase 2 — customers and products. Loaded only when their tab is opened, because both reach
    // ConnectWise and there is no reason to do that while somebody is editing a connection.
    const [customers, setCustomers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [agreements, setAgreements] = useState([]);
    const [loadingAgreements, setLoadingAgreements] = useState(false);
    const [editingCustomerId, setEditingCustomerId] = useState(null);
    const [draft, setDraft] = useState({});
    const [savingMapping, setSavingMapping] = useState(false);

    const [productSummary, setProductSummary] = useState(null);
    const [cwProducts, setCwProducts] = useState([]);
    const [savingProductKey, setSavingProductKey] = useState(null);

    // Phase 3 — preview, activation and presence.
    const [dryRun, setDryRun] = useState(null);
    const [preparing, setPreparing] = useState(false);
    const [syncState, setSyncState] = useState(null);
    const [pendingWork, setPendingWork] = useState(null);
    const [activationBusy, setActivationBusy] = useState(false);
    // N3 — whether the Sync tab asks for discarded lines as well. Off by default, as the status
    // screen's "Show acknowledged" is, so the ordinary view is unchanged.
    const [showDiscarded, setShowDiscarded] = useState(false);
    // Whether the Sync tab's own read of the preview failed. Without it a failed read looked like
    // having no preview, and "Queue for syncing" asked the reseller to prepare one they already had.
    const [previewLoadFailed, setPreviewLoadFailed] = useState(false);
    const [presence, setPresence] = useState(null);

    const [resellers, setResellers] = useState([]);
    const [selectedReseller, setSelectedReseller] = useState(null);

    // THE SERVER DECIDES WHETHER THIS PICKER EXISTS, not sessionStorage.
    //
    // isMasterAdministrator() reads a session value the server does not trust (see its comment), and
    // the two can disagree: the portal rendered the picker while MasterAdmin/GetResellers answered
    // 403 "ResellerID mismatch", leaving a control that could not be used and an error on every
    // load. The session value is now only a cheap pre-filter that decides whether the call is worth
    // making at all; whether the picker RENDERS follows from what came back.
    const [canChooseReseller, setCanChooseReseller] = useState(false);
    // The session says master account but the server would not confirm it. Said on screen rather
    // than hiding the picker silently, which is how N13 went unnoticed.
    const [pickerUnavailable, setPickerUnavailable] = useState(false);

    // N13 — a master administrator starts with nothing chosen, so nothing loads until they choose.
    // Their own reseller is one of the choices. Everyone else is never waiting: they have no picker.
    const awaitingChoice = canChooseReseller && !selectedReseller;

    // The master administrator's support role: working on another reseller, they may view, test and
    // save the connection and switch sync off, but not change mappings, prepare a preview or touch
    // billing lines. The server refuses those; this only stops the screen offering them.
    //
    // Derived here rather than read from a response because it mirrors the server exactly: the
    // picker exists only once the server has confirmed a master administrator, and the server marks
    // a request on-behalf precisely when the chosen reseller is not their own.
    const ownResellerId = sessionStorage.getItem("ResellerID");
    const isActingOnBehalf = canChooseReseller && !!selectedReseller
        && String(selectedReseller) !== String(ownResellerId);

    /**
     * Each tab's own load state — { busy, error } keyed by TAB value.
     *
     * ONE OBJECT RATHER THAN EIGHT useStates, so the tabs stay symmetrical and adding one does not
     * mean remembering two more declarations. Per-tab rather than page-level because a failure on
     * Products must not blank Customers, and a retry must not reset the page: the client asked for
     * the current tab, context and anything typed to survive one.
     */
    const [tabState, setTabState] = useState({});

    // Whether a preview is already on screen, read inside loadDryRun without making it a dependency.
    const hasDryRun = useRef(false);

    /**
     * Whether `config` describes the reseller currently selected.
     *
     * A REF BECAUSE IT HAS TO BE TRUE OR FALSE WITHIN THE SAME COMMIT THAT SETS IT. The tab
     * dispatch effect below gates on `loading`, which loadData sets true — but a state write does
     * not change the value already captured in the effect's closure for the render being committed.
     * So on a reseller switch the dispatch effect still read `loading === false`, fired a load,
     * then fired the same load again when `loading` came back down: two identical requests per
     * endpoint, per switch.
     *
     * Cleared by the reseller-change effect, which is declared before the dispatch effect and so
     * runs first, and set once the config for the new reseller is actually in hand.
     */
    const configReady = useRef(false);

    // All four are useCallback with EMPTY deps so their identity never changes. They are in the
    // dependency arrays of the load callbacks below, which are themselves in the dependency array
    // of the effect that calls them — a helper re-created each render would give every load a new
    // identity on every render and the effect would never stop firing.
    const patchTabState = useCallback((key, patch) =>
        setTabState((current) => ({ ...current, [key]: { ...current[key], ...patch } })), []);

    const beginTabLoad = useCallback(
        (key) => patchTabState(key, { busy: true, error: null }), [patchTabState]);
    const endTabLoad = useCallback(
        (key) => patchTabState(key, { busy: false }), [patchTabState]);

    const tabBusy = (key) => !!(tabState[key] && tabState[key].busy);
    const tabError = (key) => (tabState[key] && tabState[key].error) || null;

    /**
     * What every tab does with a failed load.
     *
     * handleApiError returns null ONLY for a 440, where it is already navigating to the timeout
     * page — setting an error state then would flash a panel on the way out.
     *
     * The toast is kept alongside the panel, as asked, but carries a stable id per tab so a
     * repeating failure replaces its predecessor instead of stacking. Two of these loads poll on a
     * three-second timer and the container only holds four.
     */
    const failTabLoad = useCallback((key, err, fallback, options) => {
        const message = BillingSyncApi.handleApiError(err, fallback);
        if (!message) return;
        if (options && options.silent) return;
        patchTabState(key, { error: message });
        toast.error(message, { toastId: `cws-${key}` });
    }, [patchTabState]);

    /**
     * The connection load is the one every other tab depends on, so its failure is held separately.
     *
     * Without it, a failed load left config null, which reads as "not configured" — the tab strip
     * silently collapsed to a single tab and the screen explained itself with a toast. The other
     * tabs need to be able to say "we could not check" rather than "you have not set this up".
     */
    const [configError, setConfigError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = await setAuthContext(new Date());
                const orgId = sessionStorage.getItem("orgId");
                const response = await Axios.get(`${API_ROUTE}${Routes.VALIDATE_TOKEN}/${orgId}`, {
                    headers: {
                        Authorisation: `Bearer ${token}`,
                        CloudCoverToken: sessionStorage.getItem("CloudCoverToken"),
                    },
                });
                if (!cancelled && !JSON.parse(response.data)) window.location.href = "/login";
            } catch (err) {
                if (err?.response?.status === 440) window.location.href = "/TimeoutErrorHandler";
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [setAuthContext]);

    useEffect(() => {
        // sessionStorage decides only whether the question is worth ASKING. It is a value the
        // browser holds, so it settles nothing on its own.
        if (!isMasterAdministrator()) return;
        let cancelled = false;
        (async () => {
            try {
                // THE SERVER'S OWN TEST, asked before the picker is offered rather than after it is
                // used. The reseller list alone was not enough: it is gated on a signed claim, while
                // acting on another reseller also requires the identity to be confirmed against the
                // user store. A master administrator who failed only the second was shown a picker
                // that answered "you may only work on your own reseller" on first use.
                const scope = await BillingSyncApi.getScope(setAuthContext);
                if (cancelled) return;
                if (!scope || !scope.canActOnBehalf) {
                    setResellers([]);
                    setCanChooseReseller(false);
                    setPickerUnavailable(true);
                    return;
                }

                const result = await BillingSyncApi.getResellers(setAuthContext);
                const list = Array.isArray(result) ? result : [];
                if (cancelled) return;
                setResellers(list);
                setCanChooseReseller(list.length > 0);
            } catch (err) {
                if (cancelled) return;
                setResellers([]);
                setCanChooseReseller(false);
                // A refusal is an answer, not a fault: this user may not choose a reseller, so the
                // picker is not offered — but the screen says so. Anything else — a timeout, a 500 —
                // is worth a toast, because the control is missing for a reason that may not last.
                const status = err?.response?.status;
                if (status === 401 || status === 403) {
                    setPickerUnavailable(true);
                    return;
                }
                // Covers BOTH calls above. The list is only fetched once the probe has said yes, so
                // a failure here may be either one, and naming the list would be wrong half the time.
                const message = BillingSyncApi.handleApiError(
                    err, "Could not check which resellers you can work on.");
                if (message) toast.error(message, { toastId: "cws-resellers" });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [setAuthContext]);

    const loadData = useCallback(async () => {
        setLoading(true);
        setConfigError(null);
        try {
            const result = await BillingSyncApi.getConfig(setAuthContext, selectedReseller);
            setConfig(result);
            // The private key is never returned, so the field always starts blank. Everything else is
            // seeded from what is stored, so an edit is an edit rather than a re-entry.
            setForm({
                siteUrl: result.siteUrl || "",
                companyId: result.companyId || "",
                publicKey: result.publicKey || "",
                privateKey: "",
                clientId: result.clientId || "",
            });
            // Only now may the tabs load: what they read through is the connection just fetched.
            configReady.current = true;
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "Failed to load your ConnectWise settings.");
            if (message) {
                setConfigError(message);
                toast.error(message, { toastId: "cws-connection" });
            }
        } finally {
            setLoading(false);
        }
    }, [setAuthContext, selectedReseller]);

    useEffect(() => {
        if (awaitingChoice) return;
        loadData();
    }, [loadData, awaitingChoice]);

    const loadCustomers = useCallback(async () => {
        beginTabLoad(TAB.CUSTOMERS);
        try {
            const [list, companyList] = await Promise.all([
                BillingSyncApi.getCustomers(setAuthContext, selectedReseller),
                BillingSyncApi.getCompanies(setAuthContext, selectedReseller),
            ]);
            setCustomers(Array.isArray(list) ? list : []);
            setCompanies(Array.isArray(companyList) ? companyList : []);
        } catch (err) {
            failTabLoad(TAB.CUSTOMERS, err, "Could not load your customers.");
        } finally {
            endTabLoad(TAB.CUSTOMERS);
        }
    }, [setAuthContext, selectedReseller, beginTabLoad, endTabLoad, failTabLoad]);

    const loadProducts = useCallback(async () => {
        beginTabLoad(TAB.PRODUCTS);
        try {
            const [summary, catalogue] = await Promise.all([
                BillingSyncApi.getProductMappings(setAuthContext, selectedReseller),
                BillingSyncApi.getCwProducts(setAuthContext, selectedReseller),
            ]);
            setProductSummary(summary);
            setCwProducts(Array.isArray(catalogue) ? catalogue : []);
        } catch (err) {
            failTabLoad(TAB.PRODUCTS, err, "Could not load your product mappings.");
        } finally {
            endTabLoad(TAB.PRODUCTS);
        }
    }, [setAuthContext, selectedReseller, beginTabLoad, endTabLoad, failTabLoad]);

    const loadDryRun = useCallback(async () => {
        beginTabLoad(TAB.PREVIEW);
        try {
            const result = await BillingSyncApi.getDryRun(setAuthContext, selectedReseller);
            setDryRun(result);
            hasDryRun.current = !!result;
            setPreviewLoadFailed(false);
        } catch (err) {
            // THIS ONE IS ALSO THE POLL TARGET, every three seconds while a preview is being
            // prepared. A tick that fails must not blank a preview already on screen, so the error
            // state is only taken when there is nothing to fall back on — otherwise the figures
            // stay and the next tick quietly recovers.
            //
            // A ref, not the dryRun state: reading the state would put it in this callback's
            // dependencies, and the effect below re-runs whenever this callback's identity changes.
            // Loading a preview would then change dryRun, change this identity, re-fire the effect
            // and load it again, for ever.
            failTabLoad(TAB.PREVIEW, err, "Could not load your preview.",
                { silent: hasDryRun.current });
        } finally {
            endTabLoad(TAB.PREVIEW);
        }
    }, [setAuthContext, selectedReseller, beginTabLoad, endTabLoad, failTabLoad]);

    const loadActivation = useCallback(async () => {
        beginTabLoad(TAB.ACTIVATION);
        try {
            const [state, work, preview] = await Promise.all([
                BillingSyncApi.getSyncState(setAuthContext, selectedReseller),
                BillingSyncApi.getPendingWork(setAuthContext, selectedReseller, showDiscarded),
                // "Queue for syncing" lives on this tab but queues the preview, so the preview has
                // to be known here too. A failure does not fail the whole tab — discarding and
                // switching sync off have nothing to do with the preview — but it is remembered, so
                // the button can say why it is unavailable rather than guess.
                BillingSyncApi.getDryRun(setAuthContext, selectedReseller).catch(() => undefined),
            ]);
            setSyncState(state);
            setPendingWork(work);
            setPreviewLoadFailed(preview === undefined);
            if (preview !== undefined) {
                setDryRun(preview);
                hasDryRun.current = !!preview;
            }
            // Nothing left discarded — after rebuilding the last of them — leaves nothing to show,
            // so the toggle goes back off rather than staying ticked against "(0)".
            if (!work || !(work.discardedCount > 0)) setShowDiscarded(false);
        } catch (err) {
            failTabLoad(TAB.ACTIVATION, err, "Could not load your sync status.");
        } finally {
            endTabLoad(TAB.ACTIVATION);
        }
    }, [setAuthContext, selectedReseller, showDiscarded, beginTabLoad, endTabLoad, failTabLoad]);

    // Every tab after Connection reads through the saved connection, so until there is one they would
    // only fail. A reseller switch can land on one that is not set up, so fall back to Connection.
    const isConfigured = !!(config && config.isConfigured);

    // Only when we actually KNOW there is no connection. A load that failed knows nothing, and
    // throwing the reseller back to the Connection tab on a transient error would lose their place.
    useEffect(() => {
        if (!isConfigured && !configError && tab !== TAB.CONNECTION) setTab(TAB.CONNECTION);
    }, [isConfigured, configError, tab]);

    /**
     * A DIFFERENT RESELLER MEANS DIFFERENT DATA, AND NONE OF WHAT IS ON SCREEN DESCRIBES IT.
     *
     * Nothing used to be cleared, so switching reseller left the previous one's customers, product
     * mappings and preview rendered under the new one's name until each fetch returned — five to
     * eighteen seconds on demo, with no indication any of it was stale. A master administrator
     * comparing two resellers had no way to tell which one they were looking at.
     *
     * DECLARED BEFORE THE DISPATCH EFFECT BELOW so it commits first, and marks every data tab busy
     * in the same commit. Clearing alone is not enough: cleared data with busy still false renders
     * the EMPTY state — "You have no customers yet." — for the gap between the reseller changing
     * and the dispatch effect firing, which is the exact message this release exists to stop
     * showing mid-fetch. The page-level `loading` gate below widens that gap, because loadData is
     * re-created by the same change and sets it true again.
     *
     * All four, not just the visible one: an unvisited tab still holds the old reseller's rows, and
     * would show them the moment it was opened. Its own beginTabLoad/endTabLoad takes over then.
     *
     * The ref skips the first run — the same idiom as hasDryRun above and hasLoadedOnce on the
     * status screen. Without it this fires on mount and clears state that is already empty.
     */
    const lastReseller = useRef(selectedReseller);
    useEffect(() => {
        if (lastReseller.current === selectedReseller) return;
        lastReseller.current = selectedReseller;

        setCustomers([]);
        setCompanies([]);
        setAgreements([]);
        setProductSummary(null);
        setCwProducts([]);
        setDryRun(null);
        setSyncState(null);
        setPendingWork(null);
        setShowDiscarded(false);
        setPreviewLoadFailed(false);

        // An edit in progress belongs to the reseller it was started on.
        setEditingCustomerId(null);
        setDraft({});

        hasDryRun.current = false;

        // The config on screen still describes the PREVIOUS reseller until loadData replaces it,
        // and the dispatch effect below cannot see that from `loading` alone — see configReady.
        configReady.current = false;

        setTabState({
            [TAB.CUSTOMERS]: { busy: true, error: null },
            [TAB.PRODUCTS]: { busy: true, error: null },
            [TAB.PREVIEW]: { busy: true, error: null },
            [TAB.ACTIVATION]: { busy: true, error: null },
        });
    }, [selectedReseller]);

    useEffect(() => {
        // configReady first: `loading` is a render behind on the commit where the reseller changes,
        // and acting on it there fetched everything twice.
        if (awaitingChoice || !configReady.current || loading || !isConfigured) return;
        if (tab === TAB.CUSTOMERS) loadCustomers();
        if (tab === TAB.PRODUCTS) loadProducts();
        if (tab === TAB.PREVIEW) loadDryRun();
        if (tab === TAB.ACTIVATION) loadActivation();
    }, [awaitingChoice, loading, isConfigured, tab, loadCustomers, loadProducts, loadDryRun, loadActivation]);

    /**
     * A preview is prepared in the background, so the screen has to come back and look.
     *
     * Only while one is actually being prepared, and only on the tabs that show it — its own, and
     * Sync, whose "Queue for syncing" waits on it. A page left open on the connection form has no
     * reason to be asking.
     */
    useEffect(() => {
        if (tab !== TAB.PREVIEW && tab !== TAB.ACTIVATION) return undefined;
        if (!dryRun || !dryRun.isPreparing) return undefined;

        const timer = setTimeout(loadDryRun, DRY_RUN_POLL_MS);
        return () => clearTimeout(timer);
    }, [tab, dryRun, loadDryRun]);

    /**
     * Presence — says somebody is here, and finds out who else is.
     *
     * Advisory, as the client asked: it names the other person and lets either of them carry on.
     * The place is given up on the way out so the next arrival is not warned about somebody who
     * has already gone.
     */
    useEffect(() => {
        // Not present on any reseller until one is chosen — announcing ourselves on the master
        // account's own would warn its users about somebody who is not working on it.
        if (awaitingChoice) return undefined;
        let cancelled = false;

        async function touch() {
            try {
                const result = await BillingSyncApi.touchPresence(setAuthContext, selectedReseller);
                if (!cancelled) setPresence(result);
            } catch (err) {
                // A warning nobody can show is not worth a message of its own. Editing still works.
            }
        }

        touch();
        const timer = setInterval(touch, PRESENCE_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(timer);
            BillingSyncApi.releasePresence(setAuthContext, selectedReseller).catch(() => {});
        };
    }, [setAuthContext, selectedReseller, awaitingChoice]);

    function beginEdit(customer) {
        setEditingCustomerId(customer.customerOrgId);
        setDraft({
            cwCompanyId: customer.cwCompanyId || null,
            cwCompanyName: customer.cwCompanyName || null,
            cwAgreementId: customer.cwAgreementId || null,
            cwAgreementName: customer.cwAgreementName || null,
            pricingMode: customer.pricingMode || "cloudcover",
        });
        setAgreements([]);

        // Load the company's agreements so the dropdown has options, but keep the one already
        // chosen. Treating an edit as a company change would make somebody re-pick the agreement
        // just to alter who owns pricing.
        if (customer.cwCompanyId) loadAgreementsFor(customer.cwCompanyId);
    }

    /** Fetches a company's agreements without disturbing the draft. */
    async function loadAgreementsFor(companyId) {
        if (!companyId) {
            setAgreements([]);
            return;
        }

        setLoadingAgreements(true);
        try {
            const list = await BillingSyncApi.getAgreements(setAuthContext, companyId, selectedReseller);
            setAgreements(Array.isArray(list) ? list : []);
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "Could not load that company's agreements.");
            if (message) toast.error(message, { toastId: "cws-agreements" });
        } finally {
            setLoadingAgreements(false);
        }
    }

    /**
     * A deliberate company change. This is the one place a live ConnectWise read belongs, and the
     * one place clearing the agreement is right — the previous one belonged to the previous company.
     */
    async function chooseCompany(companyId) {
        const chosen = companies.find((c) => c.id === companyId);
        setDraft((d) => ({
            ...d,
            cwCompanyId: companyId,
            cwCompanyName: chosen ? chosen.name : null,
            cwAgreementId: null,
            cwAgreementName: null,
        }));

        await loadAgreementsFor(companyId);
    }

    async function saveCustomerMapping() {
        setSavingMapping(true);
        try {
            await BillingSyncApi.saveCustomerMapping(setAuthContext, {
                customerOrgId: editingCustomerId,
                ...draft,
            }, selectedReseller);
            toast.success("Customer mapping saved.");
            setEditingCustomerId(null);
            await loadCustomers();
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "The mapping could not be saved.");
            if (message) toast.error(message, { toastId: "cws-save-customer" });
        } finally {
            setSavingMapping(false);
        }
    }

    async function saveProductMapping(mapping) {
        setSavingProductKey(mapping.sourceProductKey);
        try {
            await BillingSyncApi.saveProductMapping(setAuthContext, mapping, selectedReseller);
            toast.success("Product mapping saved.");
            await loadProducts();
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "The mapping could not be saved.");
            if (message) toast.error(message, { toastId: "cws-save-product" });
        } finally {
            setSavingProductKey(null);
        }
    }

    /**
     * Any edit invalidates the last test result.
     *
     * This is what stops a reseller testing one set of details, changing the company id, and saving
     * against a pass that no longer describes what is in the form.
     */
    function handleFormChange(next) {
        setForm(next);
        setTestResult(null);
    }

    async function handleTest() {
        setTesting(true);
        setTestResult(null);
        try {
            const result = await BillingSyncApi.testConnection(setAuthContext, form, selectedReseller);
            setTestResult(result);
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "The connection test could not be run.");
            if (message) setTestResult({ success: false, message });
        } finally {
            setTesting(false);
        }
    }

    async function handleSave() {
        setSaving(true);
        try {
            // A blank key means "keep the stored one". Sending the field as typed preserves that,
            // since the server treats blank the same way.
            const result = await BillingSyncApi.saveConfig(setAuthContext, form, selectedReseller);
            setTestResult(result);
            toast.success("ConnectWise connection saved.");
            await loadData();
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "The connection could not be saved.");
            if (message) {
                setTestResult({ success: false, message });
                toast.error(message, { toastId: "cws-save-connection" });
            }
        } finally {
            setSaving(false);
        }
    }

    // ---- Phase 3 handlers ----

    async function handlePrepareDryRun() {
        setPreparing(true);
        try {
            const result = await BillingSyncApi.prepareDryRun(setAuthContext, selectedReseller);
            // The notice is for a wait, so it is dismissed once there is something to look at —
            // a preview that returns in under a second was leaving "this can take a moment" sitting
            // over the page after the figures had already arrived. The panel's own "Preparing…"
            // covers the slow case, so nothing is lost by retiring the toast here.
            const notice = result && result.message ? toast.info(result.message) : null;
            // Mark it as under way at once, so the panel says "preparing" rather than "none" until
            // the first poll comes back.
            setDryRun((current) => ({ ...(current || {}), isPreparing: true }));
            await loadDryRun();
            if (notice !== null) toast.dismiss(notice);
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, "The preview could not be prepared.");
            if (message) toast.error(message, { toastId: "cws-prepare" });
        } finally {
            setPreparing(false);
        }
    }

    async function runActivationAction(action, fallbackMessage) {
        setActivationBusy(true);
        try {
            const result = await action();
            if (result && result.message) toast.success(result.message);
            await loadActivation();
        } catch (err) {
            const message = BillingSyncApi.handleApiError(err, fallbackMessage);
            if (message) toast.error(message, { toastId: "cws-activation-action" });
            // Reloaded even on failure: a refusal usually means the screen is out of date, and
            // showing why is more use than leaving the old picture in place.
            await loadActivation();
        } finally {
            setActivationBusy(false);
        }
    }

    const handleEnable = () => runActivationAction(
        () => BillingSyncApi.enableSync(setAuthContext, selectedReseller),
        "Sync could not be switched on.");

    const handleDisable = () => runActivationAction(
        () => BillingSyncApi.disableSync(setAuthContext, selectedReseller),
        "Sync could not be switched off.");

    const handleDiscard = (ids) => runActivationAction(
        () => BillingSyncApi.discardWork(setAuthContext, ids, selectedReseller),
        "That work could not be discarded.");

    // N4 — the chosen lines, as Discard takes them.
    const handleRebuild = (ids) => runActivationAction(
        () => BillingSyncApi.rebuildWork(setAuthContext, ids, selectedReseller),
        "The discarded work could not be rebuilt.");

    // N1 — "Queue for syncing", on the Sync tab. Reloading that tab reloads the preview with it:
    // the number still awaiting approval is what decides whether the button is offered again.
    //
    // dryRun.preparedAt is passed exactly as the server sent it — see BillingSyncApi.queueForSync.
    const handleQueueForSync = () => runActivationAction(
        () => BillingSyncApi.queueForSync(
            setAuthContext, dryRun && dryRun.preparedAt, selectedReseller),
        "Your preview could not be queued for syncing.");

    // billing-sync-page bounds this screen to the window so its content can scroll; see general.css.
    // The navigation and the title sit outside the scrolling part and stay put.
    return (
        <div className="billing-sync-page">
            <NavBar page="connectWiseSync" authContext={authContext} />
            {/* Same header as the partner Organisations page (Reseller.js), so it takes the
                reseller's theme through .SectionTitle rather than Bootstrap defaults. */}
            <div className="card-header reseller-card p-1">
                <span className="SectionTitle">CONNECTWISE SYNC</span>
            </div>
            <div className="billing-sync-content" style={{ padding: 20 }}>
                {/* Advisory, never a lock. Naming them is what lets the two of them sort it out. */}
                {presence && presence.somebodyElseIsHere && (
                    <div className="alert alert-warning py-2" style={{ fontSize: 14 }}>
                        <span className="fw-semibold">{presence.userEmail}</span> is also working on
                        these settings. You can carry on, but the last change saved is the one that
                        will stand.
                    </div>
                )}

                {canChooseReseller && (
                    <ResellerPicker
                        inputId="cw-reseller"
                        resellers={resellers}
                        ownId={ownResellerId}
                        value={selectedReseller}
                        onChange={(id) => {
                            setSelectedReseller(id);
                            // A different reseller means a different connection, so anything the
                            // form was holding no longer describes what is on screen.
                            setTestResult(null);
                        }}
                        hint={isActingOnBehalf
                            ? "Support view: you can view, test and update this reseller's connection, and switch its sync off. Mappings, previews and billing lines stay with the reseller."
                            : "Choose any reseller to view and test its ConnectWise setup."}
                    />
                )}
                {pickerUnavailable && <ResellerPickerUnavailable />}

                {awaitingChoice ? (
                    <ChooseResellerPrompt detail="Select a reseller above to view and test its ConnectWise setup." />
                ) : (
                <>
                <ul className="nav nav-tabs mb-3">
                    {[
                        [TAB.CONNECTION, "Connection"],
                        [TAB.CUSTOMERS, "Customers & agreements"],
                        [TAB.PRODUCTS, "Products"],
                        [TAB.PREVIEW, "Preview"],
                        [TAB.ACTIVATION, "Sync"],
                        // Tabs stay visible when the connection could not be CHECKED. Hiding them
                        // on a failed load told the reseller they had not set anything up, which is
                        // a different thing and usually untrue.
                    ].filter(([value]) => isConfigured || configError || value === TAB.CONNECTION)
                        .map(([value, label]) => (
                        <li className="nav-item" key={value}>
                            <button
                                type="button"
                                className={`nav-link${tab === value ? " active" : ""}`}
                                onClick={() => setTab(value)}
                            >
                                {label}
                            </button>
                        </li>
                    ))}
                </ul>

                {/* Suppressed when the load failed: "save the connection" is the wrong advice for
                    somebody whose connection we simply could not read. */}
                {!loading && !isConfigured && !configError && (
                    <p className="text-muted" style={{ fontSize: 14 }}>
                        Customers &amp; agreements, Products, Preview and Sync become available once the
                        connection is saved.
                    </p>
                )}

                {!loading && configError && tab !== TAB.CONNECTION && (
                    <p className="text-muted" style={{ fontSize: 14 }}>
                        Your ConnectWise connection could not be checked, so this tab may be
                        incomplete. Open the Connection tab to try again.
                    </p>
                )}

                {tab === TAB.CUSTOMERS && (
                    <CustomerAgreementGrid
                        customers={customers}
                        companies={companies}
                        agreements={agreements}
                        loadingAgreements={loadingAgreements}
                        editingCustomerId={editingCustomerId}
                        draft={draft}
                        saving={savingMapping}
                        onBeginEdit={beginEdit}
                        onCancelEdit={() => setEditingCustomerId(null)}
                        onDraftChange={setDraft}
                        onCompanyChosen={(id) => chooseCompany(id)}
                        onSave={saveCustomerMapping}
                        loading={tabBusy(TAB.CUSTOMERS)}
                        error={tabError(TAB.CUSTOMERS)}
                        onRetry={loadCustomers}
                        readOnly={isActingOnBehalf}
                    />
                )}

                {tab === TAB.PRODUCTS && (
                    <ProductMappingGrid
                        summary={productSummary}
                        products={cwProducts}
                        saving={savingProductKey != null}
                        savingKey={savingProductKey}
                        onSave={saveProductMapping}
                        loading={tabBusy(TAB.PRODUCTS)}
                        error={tabError(TAB.PRODUCTS)}
                        onRetry={loadProducts}
                        readOnly={isActingOnBehalf}
                    />
                )}

                {tab === TAB.PREVIEW && (
                    <DryRunPanel
                        summary={dryRun}
                        preparing={preparing}
                        onPrepare={handlePrepareDryRun}
                        loading={tabBusy(TAB.PREVIEW)}
                        error={tabError(TAB.PREVIEW)}
                        onRetry={loadDryRun}
                        readOnly={isActingOnBehalf}
                    />
                )}

                {tab === TAB.ACTIVATION && (
                    <ActivationPanel
                        state={syncState}
                        pendingWork={pendingWork}
                        busy={activationBusy}
                        onEnable={handleEnable}
                        onDisable={handleDisable}
                        onDiscard={handleDiscard}
                        onRebuild={handleRebuild}
                        preview={dryRun}
                        previewLoadFailed={previewLoadFailed}
                        onQueue={handleQueueForSync}
                        showDiscarded={showDiscarded}
                        onToggleDiscarded={setShowDiscarded}
                        loading={tabBusy(TAB.ACTIVATION)}
                        error={tabError(TAB.ACTIVATION)}
                        onRetry={loadActivation}
                        readOnly={isActingOnBehalf}
                    />
                )}

                {tab === TAB.CONNECTION && (loading ? (
                    <PanelState busy />
                ) : configError ? (
                    <PanelState
                        error={configError}
                        actionLabel="Try again"
                        busyLabel="Trying…"
                        onAction={loadData}
                    />
                ) : (
                    <ConnectionForm
                        form={form}
                        onChange={handleFormChange}
                        hasStoredKey={!!(config && config.hasPrivateKey)}
                        testResult={testResult}
                        testing={testing}
                        saving={saving}
                        onTest={handleTest}
                        onSave={handleSave}
                    />
                ))}
                </>
                )}
            </div>
        </div>
    );
}
